import{ useEffect, useState } from "react";
import "./Courses.css";
import { Card, message, Input, Spin, Empty, Pagination } from "antd";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BaseUrl, CommuncationBot } from "../Constants/Constants";
import { useStateValue } from "../../Reducer/reducer";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SearchIcon from "@mui/icons-material/Search";

const Courses = () => {
  const { Search } = Input;
  const navigate = useNavigate();

  const [{ UserID, companyId }, dispatch] = useStateValue();

  const [courses, setCourses] = useState([]);
  const [coursesarr, setCoursesarr] = useState([]);
  const [loading, setLoading] = useState(true);
  const pageSize = 8;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (UserID != undefined && UserID != "") {
      setLoading(true);

      let body = {
        userId: UserID
      };

      axios.post(`${BaseUrl}course-mapping/getAllCourses`, body, { withCredentials: true })
        .then((res) => {
          if (res.data.statuscode === 200) {
            setCourses(res.data.data);
            setCoursesarr(res.data.data);
            setLoading(false);
          } else {
            setCourses([]);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.log(err.message);
          setLoading(false);
          message.error('Please login again to continue!');
          navigate('/login');
          setCourses([]);
        });
    } else {
      message.error('Please login again to continue!');
      navigate('/login');
    }
  }, [UserID, navigate]);

  const handleCommunication = () => {
    const Profile = localStorage.getItem("Profile");
    const ParsedProfile = JSON.parse(Profile);
    window.location.href = `${CommuncationBot}/${ParsedProfile.email}`;
  };

  const handleCard = (e) => {
    navigate(`/courses/${e.course_id}`);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setCurrentPage(1);

    if (value.trim() === "") {
      setCoursesarr(courses);
    } else {
      const filteredResults = courses.filter((item) =>
        item.course_title.toLowerCase().includes(value.toLowerCase())
      );
      setCoursesarr(filteredResults);
    }
  };

  // Calculate pagination - Language card only shows on first page when no search
  const showLanguageCard = coursesarr.length >= courses.length && currentPage === 1;

  // Calculate indices with proper offset handling
  let startIndex, endIndex;
  
  // Check if language card is being used at all (on page 1)
  const hasLanguageCard = coursesarr.length >= courses.length;
  
  if (hasLanguageCard) {
    if (currentPage === 1) {
      // Page 1: show 7 courses (8 - 1 for language card)
      startIndex = 0;
      endIndex = pageSize - 1; 
    } else {
      startIndex = (pageSize - 1) + ((currentPage - 2) * pageSize);
      endIndex = startIndex + pageSize;
    }
  } else {
    // No language card at all (search is active): standard pagination
    startIndex = (currentPage - 1) * pageSize;
    endIndex = startIndex + pageSize;
  }

  const paginatedCourses = coursesarr.slice(startIndex, endIndex);

  // Total items includes the language card as one slot
  const totalItems = hasLanguageCard ? coursesarr.length + 1 : coursesarr.length;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="Courses-main">
      {/* Header Section */}
      <div className="coursesHeader">
        <div className="coursesHeaderContent">
          <div className="coursesHeaderIconWrapper">
            <MenuBookIcon className="coursesHeaderIcon" />
          </div>
          <div>
            <h1 className="coursesMainTitle">Active Courses</h1>
            <p className="coursesSubtitle">Explore and enhance your learning journey</p>
          </div>
        </div>
        <div className="coursesSearchWrapper">
          <Input
            placeholder="Search courses..."
            onChange={handleChange}
            className="coursesSearchInput"
            allowClear
            prefix={<SearchIcon className="searchIconPrefix" />}
            size="large"
          />
        </div>
      </div>

      {/* Content Section */}
      {loading ? (
        <div className="coursesLoader">
          <Spin size="large" />
        </div>
      ) : (
        <div className="coursesContent">
          {coursesarr.length === 0 && courses.length > 0 ? (
            <div className="coursesEmptyState">
              <Empty 
                description="No courses found matching your search"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            <>
              {courses.length !== 0 && (
                <>
                  <div className="coursesGrid">
                    {/* Communication Bot Card - Only on first page, no search */}
                    {showLanguageCard && (
                      <Card
                        onClick={handleCommunication}
                        hoverable
                        className="communicationCard"
                        cover={
                          <div className="communicationCardCover">
                            <div className="communicationCardOverlay">
                              <ChatBubbleOutlineIcon className="communicationIcon" />
                            </div>
                            <img
                              alt="Communication Bot"
                              src="/icons/CourseIcons.png"
                              className="communicationCardImage"
                            />
                          </div>
                        }
                      >
                        <div className="communicationCardContent">
                          <AutoAwesomeIcon className="communicationBadge" />
                          <h3 className="communicationCardTitle">Language & Communication</h3>
                          <p className="communicationCardDescription">Enhance your communication skills</p>
                        </div>
                      </Card>
                    )}

                    {/* Course Cards */}
                    {paginatedCourses.map((item, index) => (
                      <Card
                        key={item.course_id || index}
                        onClick={() => handleCard(item)}
                        hoverable
                        className="courseCard"
                        cover={
                          <div className="courseCardCover">
                            <div className="courseCardOverlay">
                              <PlayCircleOutlineIcon className="playIcon" />
                            </div>
                            <img
                              alt="Course Image"
                              src={
                                item.imageUrl && item.imageUrl !== ""
                                  ? item.imageUrl
                                  : "/icons/CourseIcons.png"
                              }
                              className="courseCardImage"
                            />
                          </div>
                        }
                      >
                        <div className="courseCardContent">
                          <div className="courseCardHeader">
                            <MenuBookIcon className="courseCardIcon" />
                            <h3 className="courseCardTitle">
                              {item.course_title || "Unnamed Course"}
                            </h3>
                          </div>
                          <div className="courseCardFooter">
                            <span className="courseCardAction">View Course</span>
                            <AutoAwesomeIcon className="courseCardArrow" />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalItems > pageSize && (
                    <div className="coursesPaginationWrapper">
                      <Pagination
                        current={currentPage}
                        total={totalItems}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        className="coursesPagination"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Empty State */}
              {courses.length === 0 && (
                <div className="coursesEmptyState">
                  <Empty 
                    description="No courses available at the moment"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Courses;